// src/components/geofencing/GeofenceMap.js
import React, { useEffect, useRef, useState } from 'react';

/**
 * Interactive geofence editor supporting two shapes:
 *  - circle:  click/drag sets the center, radius comes from a slider in the parent form
 *  - polygon: click adds a vertex; connects automatically once 3+ points exist
 */
export default function GeofenceMap({
  shape = 'circle',
  center, radiusMeters, onCenterChange,          // circle props
  points, onAddPoint,                            // polygon props
  color = '#3b82f6', height = 260, editable = true, truckPosition,
}) {
  const mapRef = useRef(null);
  const leafRef = useRef(null);
  const circleRef = useRef(null);
  const centerMarkerRef = useRef(null);
  const truckMarkerRef = useRef(null);
  const polygonRef = useRef(null);
  const vertexMarkersRef = useRef([]);
  const clickHandlerRef = useRef(null);

  // Tracks whether the Leaflet map instance actually exists yet. Drawing
  // effects depend on this (not just their own props) so that if the map
  // finishes initializing *after* the first render — e.g. Leaflet's script
  // was still loading — they get a chance to run again instead of silently
  // giving up forever, which was causing a saved circle/polygon to appear
  // "lost" when reopening the edit modal.
  const [mapReady, setMapReady] = useState(false);

  // Init map once
  useEffect(() => {
    if (!mapRef.current) return;

    // Guards the async/delayed callbacks below (requestAnimationFrame,
    // setTimeout, script onload, interval poll) so that if the component
    // unmounts — e.g. the user closes the modal — before they fire, they
    // don't go on to call methods on a Leaflet map that .remove() has
    // already destroyed. Doing so throws inside Leaflet's own code, which
    // (since it's loaded from a different origin) surfaces to the browser
    // as an opaque, unhelpful "Script error." with no details.
    let cancelled = false;

    function createMap(L) {
      if (cancelled || leafRef.current || !mapRef.current) return;
      // Defensive: Leaflet stamps a _leaflet_id onto any container it's
      // used, and refuses to initialize a second map on the same element
      // while that stamp is present. Shouldn't apply here since each open
      // gets a brand-new DOM node, but clearing it is free insurance.
      if (mapRef.current._leaflet_id) delete mapRef.current._leaflet_id;

      const startCenter = center?.lat ? [center.lat, center.lng]
        : points?.length ? points[0]
        : [14.5995, 120.9842];
      const map = L.map(mapRef.current, { center: startCenter, zoom: (center?.lat || points?.length) ? 13 : 11 });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '©OpenStreetMap contributors' }).addTo(map);
      leafRef.current = map;
      map.invalidateSize();
      setMapReady(true);
      // One more correction shortly after, in case the modal's own open
      // animation was still finishing when the map was created.
      setTimeout(() => {
        if (cancelled || !leafRef.current) return;
        map.invalidateSize();
        if (center?.lat) map.panTo([center.lat, center.lng]);
      }, 250);
    }

    // Leaflet's L.map() measures its container's pixel size the instant
    // it's called. If that happens before the browser has finished a full
    // layout/paint pass for the just-opened modal — which reliably happens
    // when window.L is *already* loaded, since then there's nothing to
    // wait on and this runs practically synchronously on mount — it
    // captures a wrong size and the circle/polygon silently fails to
    // render, even though invalidateSize() afterward doesn't fully recover
    // it. Waiting two animation frames guarantees at least one full
    // layout+paint cycle has completed first, regardless of whether
    // Leaflet needed to load over the network or was already cached — so
    // both the "first ever open" and "reopen in the same session" cases
    // behave identically instead of only one of them working.
    function init(L) {
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (cancelled) return;
        requestAnimationFrame(() => createMap(L));
      });
    }

    const L = window.L;
    if (L) {
      init(L);
    } else {
      const existing = document.querySelector('script[src*="leaflet.js"]');
      if (existing) {
        // A script tag exists but window.L isn't set yet — it may still be
        // loading, or it may have already finished (in which case 'load'
        // will never fire again). Poll briefly as a safety net either way.
        const poll = setInterval(() => {
          if (cancelled) { clearInterval(poll); return; }
          if (window.L) { clearInterval(poll); init(window.L); }
        }, 50);
        existing.addEventListener('load', () => { clearInterval(poll); if (!cancelled) init(window.L); });
        setTimeout(() => clearInterval(poll), 5000); // stop polling eventually no matter what
      } else {
        const s = document.createElement('script');
        s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        s.crossOrigin = 'anonymous'; // lets the browser report real error details instead of a masked "Script error."
        s.onload = () => { if (!cancelled) init(window.L); };
        document.head.appendChild(s);
      }
    }

    return () => {
      cancelled = true;
      if (leafRef.current) {
        leafRef.current.remove();
        leafRef.current = null;
        setMapReady(false);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach/re-attach the click handler whenever shape or callbacks change
  useEffect(() => {
    const L = window.L;
    if (!L || !leafRef.current || !editable) return;
    const map = leafRef.current;

    if (clickHandlerRef.current) map.off('click', clickHandlerRef.current);

    clickHandlerRef.current = (e) => {
      if (shape === 'polygon') {
        onAddPoint({ lat: e.latlng.lat, lng: e.latlng.lng });
      } else {
        onCenterChange({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    };
    map.on('click', clickHandlerRef.current);

    return () => { if (clickHandlerRef.current) map.off('click', clickHandlerRef.current); };
  }, [mapReady, shape, editable, onAddPoint, onCenterChange]);

  // ── Circle mode: draw/update center marker + circle ─────────────────────────
  useEffect(() => {
    const L = window.L;
    if (!L || !leafRef.current) return;
    const map = leafRef.current;

    // Clear circle layers when switching away from circle mode
    if (shape !== 'circle') {
      if (centerMarkerRef.current) { map.removeLayer(centerMarkerRef.current); centerMarkerRef.current = null; }
      if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; }
      return;
    }
    if (!center?.lat) return;
    const latlng = [center.lat, center.lng];

    if (centerMarkerRef.current) {
      centerMarkerRef.current.setLatLng(latlng);
    } else {
      const icon = L.divIcon({
        html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>`,
        className: '', iconSize: [16, 16], iconAnchor: [8, 8],
      });
      centerMarkerRef.current = L.marker(latlng, { icon, draggable: editable }).addTo(map);
      if (editable) {
        centerMarkerRef.current.on('dragend', (e) => {
          const pos = e.target.getLatLng();
          onCenterChange({ lat: pos.lat, lng: pos.lng });
        });
      }
    }

    if (circleRef.current) {
      circleRef.current.setLatLng(latlng);
      circleRef.current.setRadius(radiusMeters || 1000);
    } else {
      circleRef.current = L.circle(latlng, {
        radius: radiusMeters || 1000,
        color, weight: 2, fillColor: color, fillOpacity: 0.12,
      }).addTo(map);
    }

    map.panTo(latlng);
  }, [mapReady, shape, center, radiusMeters, color, editable, onCenterChange]);

  // ── Polygon mode: draw vertex markers + connecting shape ─────────────────────
  useEffect(() => {
    const L = window.L;
    if (!L || !leafRef.current) return;
    const map = leafRef.current;

    // Clear vertex markers always, redraw fresh (cheap — list is small)
    vertexMarkersRef.current.forEach(m => map.removeLayer(m));
    vertexMarkersRef.current = [];
    if (polygonRef.current) { map.removeLayer(polygonRef.current); polygonRef.current = null; }

    if (shape !== 'polygon') return;
    const pts = points || [];
    if (pts.length === 0) return;

    const latlngs = pts.map(p => [p.lat, p.lng]);

    // Vertex dots, numbered
    latlngs.forEach((ll, i) => {
      const icon = L.divIcon({
        html: `<div style="width:20px;height:20px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:10px;color:#fff;font-weight:800;">${i + 1}</div>`,
        className: '', iconSize: [20, 20], iconAnchor: [10, 10],
      });
      const marker = L.marker(ll, { icon }).addTo(map);
      vertexMarkersRef.current.push(marker);
    });

    // Shape: filled polygon once 3+ points, otherwise just a connecting line
    if (latlngs.length >= 3) {
      polygonRef.current = L.polygon(latlngs, { color, weight: 2, fillColor: color, fillOpacity: 0.12 }).addTo(map);
    } else if (latlngs.length === 2) {
      polygonRef.current = L.polyline(latlngs, { color, weight: 2, dashArray: '6,6' }).addTo(map);
    }

    if (latlngs.length === 1) map.panTo(latlngs[0]);
  }, [mapReady, shape, points, color]);

  // ── Live truck position marker (either mode) ──────────────────────────────
  useEffect(() => {
    const L = window.L;
    if (!L || !leafRef.current) return;
    if (!truckPosition?.lat) {
      if (truckMarkerRef.current) { leafRef.current.removeLayer(truckMarkerRef.current); truckMarkerRef.current = null; }
      return;
    }
    const latlng = [truckPosition.lat, truckPosition.lng];
    if (truckMarkerRef.current) {
      truckMarkerRef.current.setLatLng(latlng);
    } else {
      const icon = L.divIcon({
        html: `<div style="width:28px;height:28px;border-radius:50%;background:#f97316;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:13px;">🚛</div>`,
        className: '', iconSize: [28, 28], iconAnchor: [14, 14],
      });
      truckMarkerRef.current = L.marker(latlng, { icon, zIndexOffset: 1000 }).addTo(leafRef.current).bindPopup('Truck current location');
    }
  }, [mapReady, truckPosition]);

  return (
    <div style={{ height, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', position: 'relative' }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      {editable && (
        <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'var(--bgModal)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px', fontSize: 10, color: 'var(--textMuted)', zIndex: 1000 }}>
          {shape === 'polygon' ? 'Click the map to add points — need at least 3' : 'Click the map (or drag the pin) to set the center'}
        </div>
      )}
    </div>
  );
}
